import React from 'react';
import { Box, TextField, Button, IconButton, CircularProgress } from '@mui/material';
import StoryboardColumn from './StoryboardColumn';
import SendIcon from '@mui/icons-material/Send'; // Icon for the Enhance Button
import StoryImport from './StoryImport'; // Import the new component
import SpeechToText from './SpeechToText'; // Import the SpeechToText component

const Storyboard = ({
  story,
  enhancedStory,
  onConfirm,
  onRemoveDialogue,
  onAddDialogue,
  onEditDialogue,
  onAddCharacter,
  onEnhanceStory,
  isEnhancing, // Receive isEnhancing from App
  onSelectStory,
  onUpdateCharacterImage,
  assets,
  onUploadStory, // Pass the upload function
  onUpdateBackgroundImage, // Pass the new function
  onUpdateBackgroundMusic
}) => {
  const [enhancementPrompt, setEnhancementPrompt] = React.useState('');

  // Handle enhancement prompt submission
  const handleEnhancementSubmit = async () => {
    if (enhancementPrompt.trim()) {
      await onEnhanceStory(enhancementPrompt); // Call the enhancement function
      setEnhancementPrompt(''); // Clear the input
    }
  };

  const handleSpeechResult = (transcript) => {
    setEnhancementPrompt(enhancementPrompt + ' ' + transcript);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh', // Full screen height
        backgroundColor: '#87CEEB', // Light sky blue background
        position: 'relative', // For positioning fixed elements
      }}
    >
      {/* Start Story Button (Top Right) */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000, // Ensure it stays on top
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          sx={{
            py: 1.5,
            fontSize: '1rem',
          }}
        >
          Start Story
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={()=>
            {
              const storyData = JSON.stringify(story, null, 2);
              console.log(storyData);
            }
          }
          sx={{
            py: 1.5,
            fontSize: '1rem',
          }}
        >
          Export Story
        </Button>

      </Box>

      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          gap: 4,
          justifyContent: 'center',
          alignItems: 'stretch', // This makes children stretch to full height
          overflowY: 'auto',
          paddingTop: '10px',
        }}
      >
        {/* Original Story Column */}
        <StoryboardColumn
          story={story}
          isEditable={!enhancedStory}
          onRemoveDialogue={enhancedStory ? null : onRemoveDialogue}
          onAddDialogue={enhancedStory ? null : onAddDialogue}
          onEditDialogue={enhancedStory ? null : onEditDialogue}
          onAddCharacter={enhancedStory ? null : onAddCharacter}
          showSelection={!!enhancedStory}
          onSelect={() => onSelectStory(story)}
          onUpdateCharacterImage={onUpdateCharacterImage} // Pass the new function
          assets={assets}
          onUpdateBackgroundImage={onUpdateBackgroundImage} // Pass the new function
          onUpdateBackgroundMusic={onUpdateBackgroundMusic}
        />

        {/* Enhanced Story Column */}
        {enhancedStory && (
          <StoryboardColumn
            story={enhancedStory}
            isEditable={false}
            showSelection={true}
            onSelect={() => onSelectStory(enhancedStory)}
            assets={assets}
          />
        )}
      </Box>

      {/* Floating Input Prompt (Bottom Center, Double Height) */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)', // Center horizontally
          width: '80%', // 80% of screen width
          maxWidth: '800px', // Limit width for better readability
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.9)', // Semi-transparent black background
          borderRadius: 2,
          p: 2, // Increased padding for double height
          boxShadow: 3, // Add shadow for a floating effect
          zIndex: 1000, // Ensure it stays on top
        }}
      >
        {/* Enhancement Prompt (Double Height) */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Suggest enhancements (e.g., make it longer, change the location to a club)"
          value={enhancementPrompt}
          onChange={(e) => setEnhancementPrompt(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleEnhancementSubmit()}
          disabled={isEnhancing || enhancedStory} // Disable input field while enhancing
          InputProps={{
            endAdornment: (
              <SpeechToText 
                disabled = {isEnhancing || enhancedStory}
                onResult = {handleSpeechResult} 
            />
            ),
          }}
          sx={{
            backgroundColor: 'grey',
            borderRadius: 1,
            '& .MuiInputBase-root': {
              height: '64px', // Double the height of the input
            },
            '& .MuiInputBase-input': {
              color: 'white', // White text color
            },
            '& .MuiInputLabel-root': {
              color: 'white', // White placeholder text color
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)', // White border
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white', // White border on hover
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white', // White border when focused
            },
          }}
          multiline // Allow multiple lines of text
          rows={2} // Show 2 rows by default
        />

        {/* Iconized Enhance Button with Loading Indicator */}
        <IconButton
          color="primary"
          onClick={handleEnhancementSubmit}
          disabled={isEnhancing || enhancedStory} // Disable button while enhancing
          sx={{
            backgroundColor: '#007bff', // Blue background
            color: 'grey', // White icon
            '&:hover': {
              backgroundColor: '#0056b3', // Darker blue on hover
            },
          }}
        >
          {isEnhancing ? (
            <CircularProgress size={24} sx={{ color: 'white' }} /> // Show spinner when enhancing
          ) : (
            <SendIcon />
          )}
        </IconButton>

        {/* Import Button for File Upload */}
        <StoryImport
          onValidContent={onUploadStory} // Pass the upload function
          disabled={isEnhancing || enhancedStory} // Disable button while enhancing or if story is enhanced
        />
      </Box>
    </Box>
  );
};

export default Storyboard;